import { Email, User } from '@carbon/icons-react';
import {
  GitHubProvisioning,
  GitHubProvisioningStatus,
  GitHubWorkflowRunConclusion,
  GitHubWorkflowRunStatus,
  useCompleteAssessmentMutation,
  useStartAssessmentMutation,
} from '@gql/gql-types';
import { Alert, Box, Link, Typography } from '@mui/material';
import formatDuration from '@utils/format-duration';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Assessment } from '../gihub-coding-page';
import { GitHubCodingStep, getCurrentStep, RefetchFn } from '../utils';
import { LoadingButton } from './github-coding-components';

const PENDING_WORKFLOW_RUN_POLL_INTERVAL = 3000;

export const GithubCodingCurrentAction = ({
  assessment,
  provisioning,
  refetch,
  isRefetching,
}: {
  assessment: Assessment;
  provisioning?: GitHubProvisioning | null;
  refetch: RefetchFn;
  isRefetching: boolean;
}) => {
  const activeStep = getCurrentStep(assessment, provisioning);
  return (
    <Box>
      {activeStep === GitHubCodingStep.AssessmentNotStarted && (
        <StartAssessment assessment={assessment} refetch={refetch} />
      )}
      {activeStep === GitHubCodingStep.AssessmentStarted && (
        <AcceptInvite
          assessment={assessment}
          refetch={refetch}
          isRefetching={isRefetching}
        />
      )}
      {activeStep === GitHubCodingStep.InvitationAccepted && (
        <PushSomeCode refetch={refetch} isRefetching={isRefetching} />
      )}
      {activeStep === GitHubCodingStep.CodeSubmitted && (
        <CodeSubmitted
          assessment={assessment}
          provisioning={provisioning}
          refetch={refetch}
          isRefetching={isRefetching}
        />
      )}
    </Box>
  );
};

const StartAssessment = ({
  assessment,
  refetch,
}: {
  assessment: Assessment;
  refetch: RefetchFn;
}) => {
  const [startAssessment, { loading }] = useStartAssessmentMutation();
  const onStartAssessment = useCallback(async () => {
    await startAssessment({
      variables: {
        input: { id: assessment.id },
      },
    });
    await refetch({ withGitHubSync: false });
  }, [startAssessment, assessment.id, refetch]);

  return (
    <Box>
      <Typography variant='body1' sx={{ mb: 1 }}>
        Click the button below to begin your challenge.
      </Typography>
      <Typography variant='body1' sx={{ mb: 2 }}>
        You will receive an email with the invitation to a GitHub repository
        where you can work on the assessment.
      </Typography>
      <Alert severity='info' sx={{ mb: 2 }}>
        You will have {formatDuration(assessment.template.duration ?? 0)} to
        submit your solution.
      </Alert>
      <LoadingButton
        loading={loading}
        onClick={onStartAssessment}
        label='Start assessment'
        loadingText='Starting...'
      />
    </Box>
  );
};

const AcceptInvite = ({
  assessment,
  refetch,
  isRefetching,
}: {
  assessment: Assessment;
  refetch: RefetchFn;
  isRefetching: boolean;
}) => {
  const [wasLoading, setWasLoading] = useState(false);
  const handleClick = useCallback(() => {
    setWasLoading(true);
    refetch({ withGitHubSync: true });
  }, [refetch]);

  return (
    <Box>
      <Typography variant='body1' sx={{ mb: 2 }}>
        Check your email or click on the repository link above to accept the
        invitation. Once you join the repository, click the button below to
        continue.
      </Typography>
      <Box
        sx={{
          mb: 2,
          backgroundColor: 'background.default',
          py: 1,
          px: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
          <User size={20} />
          <Typography variant='subtitle1'>
            {assessment.args.githubUsername}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Email size={20} />
          <Typography variant='subtitle1'>
            {assessment.args.githubEmail}
          </Typography>
        </Box>
      </Box>
      {wasLoading && !isRefetching && (
        <Alert severity='info' sx={{ mb: 2 }}>
          We can&apos;t see that you&apos;ve accepted the invitation. Please{' '}
          <Link href='mailto:support@alignerr.com'>contact support</Link>.
        </Alert>
      )}
      <LoadingButton
        loading={isRefetching}
        onClick={handleClick}
        label='I have accepted the invitation'
      />
    </Box>
  );
};

const PushSomeCode = ({
  refetch,
  isRefetching,
}: {
  refetch: RefetchFn;
  isRefetching: boolean;
}) => {
  const [wasLoading, setWasLoading] = useState(false);
  const handleClick = useCallback(() => {
    setWasLoading(true);
    refetch({ withGitHubSync: true });
  }, [refetch]);

  return (
    <Box>
      <Typography variant='body1' sx={{ mb: 1 }}>
        Now it&apos;s time to code!
      </Typography>
      <Typography variant='body1' sx={{ mb: 2 }}>
        Clone the repository, complete the challenge, and push your changes to
        the main branch. Once you&apos;ve pushed your code, click the button
        below.
      </Typography>
      {wasLoading && !isRefetching && (
        <Alert severity='info' sx={{ mb: 2 }}>
          We can&apos;t see your code. Please{' '}
          <Link href='mailto:support@alignerr.com'>contact support</Link>.
        </Alert>
      )}
      <LoadingButton
        loading={isRefetching}
        onClick={handleClick}
        label='I have pushed my code'
      />
    </Box>
  );
};

const CodeSubmitted = ({
  assessment,
  provisioning,
  refetch,
  isRefetching,
}: {
  assessment: Assessment;
  provisioning?: GitHubProvisioning | null;
  refetch: RefetchFn;
  isRefetching: boolean;
}) => {
  const latestCommit = useMemo(() => {
    if (!provisioning?.commits?.length) {
      return null;
    }
    // Commits are guaranteed to be sorted descending by date
    return provisioning.commits[0];
  }, [provisioning]);

  const workflowRunPending = useMemo(() => {
    if (!latestCommit) {
      return false;
    }
    return (
      latestCommit.latestWorkflowRun === null ||
      latestCommit.latestWorkflowRun?.status !==
        GitHubWorkflowRunStatus.Completed
    );
  }, [latestCommit]);

  const canSubmit = useMemo(() => {
    if (!latestCommit) {
      return false;
    }
    return (
      provisioning?.status === GitHubProvisioningStatus.UserPushedCode &&
      latestCommit.latestWorkflowRun?.conclusion ===
        GitHubWorkflowRunConclusion.Success
    );
  }, [provisioning, latestCommit]);

  if (canSubmit) {
    return <CompleteAssessment id={assessment.id} refetch={refetch} />;
  }

  if (workflowRunPending) {
    return <WorkflowRunPending refetch={refetch} />;
  }

  return <NeedToFixCode refetch={refetch} isRefetching={isRefetching} />;
};

const WorkflowRunPending = ({ refetch }: { refetch: RefetchFn }) => {
  useEffect(() => {
    const interval = setInterval(() => {
      refetch({ withGitHubSync: true });
    }, PENDING_WORKFLOW_RUN_POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [refetch]);

  return (
    <Box>
      <Typography variant='body1'>
        We are running your code through our CI/CD pipeline. This should only
        take a few seconds.
      </Typography>
    </Box>
  );
};

const NeedToFixCode = ({
  refetch,
  isRefetching,
}: {
  refetch: RefetchFn;
  isRefetching: boolean;
}) => {
  const [wasLoading, setWasLoading] = useState(false);
  const handleClick = useCallback(() => {
    setWasLoading(true);
    refetch({ withGitHubSync: true });
  }, [refetch]);

  return (
    <Box>
      <Typography variant='body1' sx={{ mb: 2 }}>
        It appears that your latest commit on the main branch is not passing the
        tests. Please fix the issues and try again.
      </Typography>
      {wasLoading && !isRefetching && (
        <Alert severity='info' sx={{ mb: 2 }}>
          Validation failed on your latest commit.
        </Alert>
      )}
      <LoadingButton
        loading={isRefetching}
        onClick={handleClick}
        label='I have fixed the code'
      />
    </Box>
  );
};

const CompleteAssessment = ({
  id,
  refetch,
}: {
  id: string;
  refetch: RefetchFn;
}) => {
  const [completeAssessment, { loading }] = useCompleteAssessmentMutation();
  const onCompleteAssessment = useCallback(async () => {
    await completeAssessment({
      variables: { input: { id } },
    });
    await refetch({ withGitHubSync: false });
  }, [completeAssessment, id, refetch]);

  return (
    <Box>
      <Typography variant='body1' sx={{ mb: 1 }}>
        Great work! All checks have passed.
      </Typography>
      <Typography variant='body1' sx={{ mb: 2 }}>
        Please make sure the latest commit on the main branch contains the
        solution you want to submit, then click the button below to submit your
        assessment for evaluation.
      </Typography>
      <Alert severity='info' sx={{ mb: 2 }}>
        You can&apos;t make changes after you submit.
      </Alert>
      <LoadingButton
        loading={loading}
        onClick={onCompleteAssessment}
        label='Submit assessment'
        loadingText='Submitting...'
      />
    </Box>
  );
};
